import { useCallback, useEffect, useState } from "react";
import { Button } from "../components/ui/Button";

/**
 * Drive intake administration — #/admin/intake.
 *
 * There is one setup step: name the parent folder. Every brand's own folder is
 * created inside it automatically at signup, and setting the parent here
 * provisions every existing brand at once, so this page is mostly a health
 * view rather than a checklist.
 *
 * Watch health is the point of the table. The single worst outcome for this
 * feature is someone believing a folder is watched while it quietly does
 * nothing, so a failed poll shows its error here, and any brand still without
 * a folder is listed by name rather than merely absent.
 *
 * Manual binding survives, tucked below, for the case the automation cannot
 * cover: a brand whose files live in a folder somewhere else entirely.
 */

interface Folder {
  drive_folder_id: string;
  label: string;
  watch: boolean;
  watch_state: "ok" | "paused" | "error";
  watch_paused_reason: string | null;
  last_polled_at: string | null;
  last_poll_error: string | null;
  tenant_slug: string;
  tenant_name: string;
  created_by: "auto" | "manual";
  files_seen: string;
  open_proposals: string;
}

interface Settings {
  drive_parent_folder_id: string | null;
  last_checked_at: string | null;
  last_check_ok: boolean | null;
  last_check_error: string | null;
  script_token_set_at?: string | null;
  script_token_prefix?: string | null;
}

interface Provisioned {
  tenant_slug: string;
  status: "created" | "adopted" | "already-bound" | "failed" | "no-parent";
  drive_folder_id?: string;
  message?: string;
}

interface Sender {
  id: string;
  email: string;
  note: string | null;
  tenant_slug: string | null;
  tenant_name: string | null;
}

interface ImplicitSender {
  email: string;
  role: string;
  tenant_slug: string;
  tenant_name: string;
}

interface EmailEvent {
  received_at: string;
  from_email: string | null;
  to_address: string | null;
  tag: string | null;
  verdict: string;
  reason: string | null;
  subject: string | null;
  attachments: number;
  tenant_slug: string | null;
}

interface Subfolder {
  id: string;
  name: string;
  bound_to: string | null;
}

interface Proposal {
  id: string;
  dataset: string;
  vendor: string | null;
  confidence: "high" | "low" | "none";
  status: string;
  evidence: string[];
  detected_brand: string | null;
  detected_period: string | null;
  summary: string | null;
  already_loaded: boolean | null;
  collision_effect: string | null;
  restatement_policy: string | null;
  error: string | null;
  created_at: string;
  file_name: string;
  tenant_slug: string;
  tenant_name: string;
}

async function api(path: string, init?: RequestInit): Promise<any> {
  const res = await fetch(`/api/admin/intake${path}`, {
    ...init,
    headers: { "Content-Type": "application/json", Accept: "application/json", ...(init?.headers ?? {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(body.message || body.error || `HTTP ${res.status}`), { body });
  return body;
}

export default function AdminIntake() {
  const [folders, setFolders] = useState<Folder[]>([]);
  const [parent, setParent] = useState("");
  const [subfolders, setSubfolders] = useState<Subfolder[] | null>(null);
  const [looseFiles, setLooseFiles] = useState<string[]>([]);
  const [tenants, setTenants] = useState<{ slug: string; display_name: string }[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const [pollResult, setPollResult] = useState<string | null>(null);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [unprovisioned, setUnprovisioned] = useState<{ slug: string; display_name: string }[]>([]);
  const [parentInput, setParentInput] = useState("");
  const [provisionResult, setProvisionResult] = useState<Provisioned[] | null>(null);
  const [showManual, setShowManual] = useState(false);
  const [senders, setSenders] = useState<Sender[]>([]);
  const [implicitSenders, setImplicitSenders] = useState<ImplicitSender[]>([]);
  const [emailLog, setEmailLog] = useState<EmailEvent[]>([]);
  const [newSender, setNewSender] = useState({ email: "", tenant_slug: "" });
  const [scriptToken, setScriptToken] = useState<string | null>(null);
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [proposalNote, setProposalNote] = useState<Record<string, string>>({});
  const [proposalPeriod, setProposalPeriod] = useState<Record<string, string>>({});
  const [proposalResult, setProposalResult] = useState<Record<string, string>>({});

  const loadFolders = useCallback(async () => {
    try {
      setFolders((await api("/folders")).folders);
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      const r = await api("/settings");
      setSettings(r.settings);
      setUnprovisioned(r.unprovisioned ?? []);
      if (r.settings?.drive_parent_folder_id) setParentInput(r.settings.drive_parent_folder_id);
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  const loadSenders = useCallback(async () => {
    try {
      const r = await api("/senders");
      setSenders(r.additional ?? []);
      setImplicitSenders(r.implicit ?? []);
    } catch {
      /* the page is still useful without this panel */
    }
  }, []);

  const loadEmailLog = useCallback(async () => {
    try {
      setEmailLog((await api("/email-log")).events ?? []);
    } catch {
      /* ditto */
    }
  }, []);

  const loadProposals = useCallback(async () => {
    try {
      setProposals((await api("/proposals?status=open")).proposals ?? []);
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  useEffect(() => {
    void loadSettings();
    void loadFolders();
    void loadSenders();
    void loadEmailLog();
    void loadProposals();
    // The brand list drives the bind dropdown; a typo'd slug is a 404 that
    // wastes a round trip.
    fetch("/api/admin/tenants", { headers: { Accept: "application/json" } })
      .then((r) => (r.ok ? r.json() : { data: [] }))
      // GET /api/admin/tenants responds { data: [...] } (admin-tenants.ts), not
      // a bare array or { tenants }. Reading the wrong key here silently set
      // `tenants` to the response OBJECT, and tenants.map() threw "d.map is
      // not a function" in the minified build — a real production crash, not
      // a hypothetical.
      .then((b) => setTenants(Array.isArray(b?.data) ? b.data : []))
      .catch(() => setTenants([]));
  }, [loadFolders, loadSettings, loadSenders, loadEmailLog, loadProposals]);

  const fail = (e: unknown) => {
    const err = e as Error & { body?: { hint?: string } };
    setError(err.message);
    setHint(err.body?.hint ?? null);
  };

  const browse = async () => {
    setBusy("browse");
    setError(null);
    setHint(null);
    try {
      const r = await api(`/browse?parent=${encodeURIComponent(parent)}`);
      setSubfolders(r.subfolders);
      setLooseFiles(r.loose_files ?? []);
    } catch (e) {
      setSubfolders(null);
      fail(e);
    } finally {
      setBusy(null);
    }
  };

  const bind = async (folderId: string, tenantSlug: string, label: string) => {
    setBusy(folderId);
    setError(null);
    setHint(null);
    try {
      await api("/folders", {
        method: "POST",
        body: JSON.stringify({ drive_folder_id: folderId, tenant_slug: tenantSlug, label }),
      });
      await loadFolders();
      if (parent) await browse();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
    }
  };

  const saveParent = async () => {
    setBusy("parent");
    setError(null);
    setHint(null);
    setProvisionResult(null);
    try {
      const r = await api("/settings", {
        method: "PUT",
        body: JSON.stringify({ drive_parent_folder_id: parentInput }),
      });
      setProvisionResult(r.provisioned ?? []);
      await Promise.all([loadSettings(), loadFolders()]);
    } catch (e) {
      fail(e);
      await loadSettings();
    } finally {
      setBusy(null);
    }
  };

  const reconcile = async () => {
    setBusy("reconcile");
    setError(null);
    setProvisionResult(null);
    try {
      const r = await api("/reconcile", { method: "POST" });
      setProvisionResult(r.provisioned ?? []);
      await Promise.all([loadSettings(), loadFolders()]);
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
    }
  };

  const mintToken = async () => {
    setBusy("token");
    setError(null);
    try {
      const r = await api("/script-token", { method: "POST" });
      setScriptToken(r.token);
      await loadSettings();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
    }
  };

  const addSender = async () => {
    setBusy("sender");
    setError(null);
    try {
      await api("/senders", {
        method: "POST",
        body: JSON.stringify({
          email: newSender.email,
          ...(newSender.tenant_slug ? { tenant_slug: newSender.tenant_slug } : {}),
        }),
      });
      setNewSender({ email: "", tenant_slug: "" });
      await loadSenders();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
    }
  };

  const removeSender = async (id: string) => {
    setBusy(id);
    try {
      await api(`/senders/${id}`, { method: "DELETE" });
      await loadSenders();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
    }
  };

  const poll = async () => {
    setBusy("poll");
    setError(null);
    setPollResult(null);
    try {
      const r = await api("/poll", { method: "POST" });
      setPollResult(
        r.skipped_locked
          ? "Another poll is already running; this one stood down. Try again in a moment."
          : `${r.folders_polled} folder(s), ${r.files_seen} file(s) seen, ${r.files_new} new, ` +
            `${r.proposals_created} proposal(s), ${r.unrecognized} unrecognized, ${r.quarantined} quarantined.` +
            (r.errors?.length ? ` ${r.errors.length} folder error(s) — see the table below.` : "")
      );
      await Promise.all([loadFolders(), loadProposals()]);
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
    }
  };

  const confirmProposal = async (id: string) => {
    setBusy(`confirm-${id}`);
    setProposalResult((r) => ({ ...r, [id]: "" }));
    try {
      const period = proposalPeriod[id];
      const note = proposalNote[id];
      const result = await api(`/proposals/${id}/confirm`, {
        method: "POST",
        body: JSON.stringify({ ...(period ? { period } : {}), ...(note ? { note } : {}) }),
      });
      setProposalResult((r) => ({ ...r, [id]: result.summary || "Loaded." }));
      await loadProposals();
    } catch (e) {
      const err = e as Error & { body?: { message?: string } };
      setProposalResult((r) => ({ ...r, [id]: err.body?.message || err.message }));
    } finally {
      setBusy(null);
    }
  };

  const rejectProposalRow = async (id: string) => {
    setBusy(`reject-${id}`);
    try {
      await api(`/proposals/${id}/reject`, {
        method: "POST",
        body: JSON.stringify({ note: proposalNote[id] || undefined }),
      });
      await loadProposals();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Drive intake</h2>
        <p className="text-sm text-gray-600 mt-1">
          A watched Drive folder per brand, created automatically at signup. Forwarded vendor reports land there,
          get classified, and wait for review — nothing is loaded until someone confirms it.
        </p>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          <div className="font-medium">{error}</div>
          {hint && <pre className="mt-2 whitespace-pre-wrap text-xs text-red-700">{hint}</pre>}
        </div>
      )}

      {/* 1 — the one setup step */}
      <section className="rounded-lg border border-gray-200 bg-white p-5">
        <h3 className="font-medium text-gray-900">Parent folder</h3>
        <p className="text-sm text-gray-600 mt-1 mb-3">
          Each brand gets its own subfolder in here, created automatically when the brand signs up. Setting this
          also provisions every brand that already exists. The folder must be shared with the service account —
          sharing it with a person is not the same thing.
        </p>
        <div className="flex gap-2">
          <input
            value={parentInput}
            onChange={(e) => setParentInput(e.target.value)}
            placeholder="https://drive.google.com/drive/folders/…"
            className="flex-1 text-sm border border-gray-200 rounded-md px-3 py-2"
          />
          <Button onClick={saveParent} disabled={!parentInput || busy === "parent"}>
            {busy === "parent" ? "Checking…" : "Save"}
          </Button>
        </div>

        {settings?.drive_parent_folder_id && (
          <p className="mt-2 text-xs text-gray-500">
            {settings.last_check_ok === false ? (
              <span className="text-red-700">Drive rejected this folder: {settings.last_check_error}</span>
            ) : settings.last_checked_at ? (
              <>Readable, last checked {new Date(settings.last_checked_at).toLocaleString()}.</>
            ) : null}
          </p>
        )}

        {unprovisioned.length > 0 && (
          <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3">
            <div className="text-sm text-amber-900">
              {unprovisioned.length} brand{unprovisioned.length === 1 ? " has" : "s have"} no intake folder yet:{" "}
              {unprovisioned.map((u) => u.display_name).join(", ")}.
            </div>
            <div className="mt-2 flex items-center gap-3">
              <Button onClick={reconcile} disabled={busy === "reconcile"}>
                {busy === "reconcile" ? "Creating…" : "Create their folders"}
              </Button>
              <span className="text-xs text-amber-800">
                This also runs hourly on its own, so a brand created during a Drive outage catches up unattended.
              </span>
            </div>
          </div>
        )}

        {provisionResult && (
          <ul className="mt-3 text-sm space-y-1">
            {provisionResult.length === 0 && <li className="text-gray-500">Every brand already had a folder.</li>}
            {provisionResult.map((p) => (
              <li
                key={p.tenant_slug}
                className={p.status === "failed" || p.status === "no-parent" ? "text-red-700" : "text-gray-700"}
              >
                <span className="font-mono text-xs">{p.tenant_slug}</span> — {p.status}
                {p.message ? `: ${p.message}` : ""}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* 2 — what is bound, and is it healthy */}
      <section className="rounded-lg border border-gray-200 bg-white p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-medium text-gray-900">Watched folders</h3>
          <Button onClick={poll} disabled={busy === "poll" || folders.length === 0}>
            {busy === "poll" ? "Polling…" : "Poll now"}
          </Button>
        </div>

        {pollResult && (
          <p className="mb-3 text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded px-3 py-2">{pollResult}</p>
        )}

        {folders.length === 0 ? (
          <p className="text-sm text-gray-500">
            Nothing bound yet. Set a parent folder above and every brand gets one.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b border-gray-200">
                <th className="py-2">Folder</th>
                <th>Brand</th>
                <th>Watch</th>
                <th className="text-right">Files</th>
                <th className="text-right">To review</th>
                <th>Last poll</th>
              </tr>
            </thead>
            <tbody>
              {folders.map((f) => (
                <tr key={f.drive_folder_id} className="border-b border-gray-50 align-top">
                  <td className="py-2 pr-3">
                    <div className="text-gray-900">{f.label}</div>
                    <div className="text-xs text-gray-400 font-mono">{f.drive_folder_id}</div>
                  </td>
                  <td className="pr-3 text-gray-700">
                    {f.tenant_slug}
                    {f.created_by === "manual" && (
                      <span className="ml-1 text-xs text-gray-400" title="bound by hand, not provisioned">
                        (manual)
                      </span>
                    )}
                  </td>
                  <td className="pr-3">
                    <WatchBadge folder={f} />
                    {f.last_poll_error && (
                      <div className="text-xs text-red-700 mt-1 max-w-md">{f.last_poll_error}</div>
                    )}
                  </td>
                  <td className="pr-3 text-right tabular-nums text-gray-700">{f.files_seen}</td>
                  <td className="pr-3 text-right tabular-nums">
                    {Number(f.open_proposals) > 0 ? (
                      <span className="font-medium text-amber-700">{f.open_proposals}</span>
                    ) : (
                      <span className="text-gray-400">0</span>
                    )}
                  </td>
                  <td className="text-gray-600">
                    {f.last_polled_at ? new Date(f.last_polled_at).toLocaleString() : "never"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Proposals awaiting review — the actual point of the pipeline. Loading
          a brand's data is the brand's decision in the ordinary case, but a
          StoreSync admin can act here too (same as binding a folder or adding
          a sender for a brand elsewhere on this page) — see admin-intake.ts's
          header for why. */}
      <section className="rounded-lg border border-gray-200 bg-white p-5">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-medium text-gray-900">Proposals awaiting review</h3>
          <button className="text-xs text-gray-500 hover:text-gray-700" onClick={() => void loadProposals()}>
            Refresh
          </button>
        </div>
        <p className="text-sm text-gray-600 mb-3">
          What a file in a watched folder was classified as, and what loading it would do. Nothing here has touched
          a fact table — confirming runs the same writer the ingest scripts use.
        </p>

        {proposals.length === 0 ? (
          <p className="text-sm text-gray-500">Nothing waiting. Poll now, above, if you just sent something.</p>
        ) : (
          <ul className="space-y-4">
            {proposals.map((p) => (
              <li key={p.id} className="border border-gray-200 rounded-md p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm text-gray-900 font-medium">{p.file_name}</div>
                    <div className="text-xs text-gray-500">
                      {p.tenant_name} ({p.tenant_slug}) · {p.dataset}
                      {p.vendor ? ` / ${p.vendor}` : ""} · confidence: {p.confidence}
                    </div>
                  </div>
                  <span
                    className={`text-xs border rounded px-2 py-0.5 whitespace-nowrap ${
                      p.status === "needs-review" || p.status === "failed"
                        ? "text-red-700 bg-red-50 border-red-200"
                        : "text-amber-700 bg-amber-50 border-amber-200"
                    }`}
                  >
                    {p.status}
                  </span>
                </div>

                {p.summary && <p className="text-sm text-gray-700 mt-2">{p.summary}</p>}
                {p.detected_brand && (
                  <p className="text-xs text-gray-500 mt-1">File names brand: {p.detected_brand}</p>
                )}
                {p.error && <p className="text-xs text-red-700 mt-1">{p.error}</p>}
                {p.collision_effect && (
                  <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-2">
                    {p.collision_effect}
                  </p>
                )}
                {p.evidence?.length > 0 && (
                  <details className="mt-2 text-xs text-gray-500">
                    <summary className="cursor-pointer">Why it was classified this way</summary>
                    <ul className="mt-1 list-disc list-inside">
                      {p.evidence.map((e, i) => (
                        <li key={i}>{e}</li>
                      ))}
                    </ul>
                  </details>
                )}

                <div className="flex flex-wrap items-center gap-2 mt-3">
                  {/* Dutchie's export states no period anywhere; every other feed
                      reads its own, so this box is only useful for that one case. */}
                  {!p.detected_period && p.vendor === "dutchie" && (
                    <input
                      value={proposalPeriod[p.id] ?? ""}
                      onChange={(e) => setProposalPeriod((s) => ({ ...s, [p.id]: e.target.value }))}
                      placeholder="YYYY-MM-01"
                      className="text-sm border border-gray-200 rounded-md px-2 py-1 w-32"
                    />
                  )}
                  <input
                    value={proposalNote[p.id] ?? ""}
                    onChange={(e) => setProposalNote((s) => ({ ...s, [p.id]: e.target.value }))}
                    placeholder="note (optional)"
                    className="text-sm border border-gray-200 rounded-md px-2 py-1 flex-1 min-w-[10rem]"
                  />
                  <Button onClick={() => confirmProposal(p.id)} disabled={busy === `confirm-${p.id}`}>
                    {busy === `confirm-${p.id}` ? "Loading…" : "Confirm"}
                  </Button>
                  <button
                    className="text-sm text-red-700 hover:text-red-900 px-2"
                    onClick={() => rejectProposalRow(p.id)}
                    disabled={busy === `reject-${p.id}`}
                  >
                    Reject
                  </button>
                </div>

                {proposalResult[p.id] && (
                  <p className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded px-2 py-1 mt-2">
                    {proposalResult[p.id]}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Email intake — who may send, and what arrived */}
      <section className="rounded-lg border border-gray-200 bg-white p-5">
        <h3 className="font-medium text-gray-900">Email intake</h3>
        <p className="text-sm text-gray-600 mt-1 mb-3">
          Forward a vendor report to <code className="text-xs bg-gray-100 px-1 rounded">intake+&lt;brand-slug&gt;@</code>{" "}
          and it lands in that brand's folder. The tag names the brand; the sender has to be allowed to name it.
        </p>

        <div className="flex items-center gap-3 mb-4">
          <Button onClick={mintToken} disabled={busy === "token"}>
            {busy === "token" ? "Generating…" : settings?.script_token_set_at ? "Regenerate script token" : "Generate script token"}
          </Button>
          <span className="text-xs text-gray-500">
            {settings?.script_token_set_at
              ? `Set ${new Date(settings.script_token_set_at).toLocaleDateString()}. Regenerating invalidates the old one.`
              : "The Gmail script needs this to talk to StoreSync."}
          </span>
        </div>

        {scriptToken && (
          <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3">
            <div className="text-sm text-amber-900 mb-1">
              Copy this into the Apps Script's <code>STORESYNC_TOKEN</code> property now — it is not stored and
              cannot be shown again.
            </div>
            <code className="block text-xs font-mono break-all bg-white border border-amber-200 rounded px-2 py-1">
              {scriptToken}
            </code>
          </div>
        )}

        <h4 className="text-sm font-medium text-gray-900 mt-4">Allowed senders</h4>
        <p className="text-xs text-gray-500 mb-2">
          StoreSync admins may submit for any brand; a brand's own users may submit for their brand only. Add
          addresses here for an agency or vendor with no login.
        </p>

        <div className="flex gap-2 mb-3">
          <input
            value={newSender.email}
            onChange={(e) => setNewSender({ ...newSender, email: e.target.value })}
            placeholder="agency@example.com"
            className="flex-1 text-sm border border-gray-200 rounded-md px-3 py-2"
          />
          <select
            value={newSender.tenant_slug}
            onChange={(e) => setNewSender({ ...newSender, tenant_slug: e.target.value })}
            className="text-sm border border-gray-200 rounded-md px-2 py-2"
          >
            <option value="">Any brand (staff)</option>
            {tenants.map((t) => (
              <option key={t.slug} value={t.slug}>
                {t.display_name} only
              </option>
            ))}
          </select>
          <Button onClick={addSender} disabled={!newSender.email || busy === "sender"}>
            Allow
          </Button>
        </div>

        {senders.length > 0 && (
          <ul className="divide-y divide-gray-100 mb-3">
            {senders.map((s2) => (
              <li key={s2.id} className="py-2 flex items-center justify-between text-sm">
                <span>
                  <span className="text-gray-900">{s2.email}</span>{" "}
                  <span className="text-xs text-gray-500">
                    {s2.tenant_slug ? `— ${s2.tenant_name} only` : "— any brand"}
                  </span>
                </span>
                <button
                  className="text-xs text-red-700 hover:text-red-900"
                  onClick={() => removeSender(s2.id)}
                  disabled={busy === s2.id}
                >
                  Revoke
                </button>
              </li>
            ))}
          </ul>
        )}

        {implicitSenders.length > 0 && (
          <details className="text-xs text-gray-500">
            <summary className="cursor-pointer">
              Plus {implicitSenders.length} address{implicitSenders.length === 1 ? "" : "es"} allowed automatically
            </summary>
            <ul className="mt-2 space-y-0.5">
              {implicitSenders.map((i) => (
                <li key={`${i.tenant_slug}:${i.email}`}>
                  {i.email} — {i.role === "admin" ? "StoreSync admin, any brand" : `${i.tenant_name} only`}
                </li>
              ))}
            </ul>
          </details>
        )}

        <h4 className="text-sm font-medium text-gray-900 mt-5">Recent mail</h4>
        {emailLog.length === 0 ? (
          <p className="text-sm text-gray-500 mt-1">Nothing has arrived yet.</p>
        ) : (
          <table className="w-full text-sm mt-2">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b border-gray-200">
                <th className="py-2">When</th>
                <th>From</th>
                <th>Brand</th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody>
              {emailLog.slice(0, 15).map((e, i) => (
                <tr key={i} className="border-b border-gray-50 align-top">
                  <td className="py-2 pr-3 text-gray-600 whitespace-nowrap">
                    {new Date(e.received_at).toLocaleString()}
                  </td>
                  <td className="pr-3 text-gray-700">{e.from_email ?? "—"}</td>
                  <td className="pr-3 text-gray-700">{e.tenant_slug ?? e.tag ?? "—"}</td>
                  <td>
                    {e.verdict === "accepted" ? (
                      <span className="text-xs text-green-700">accepted ({e.attachments} file{e.attachments === 1 ? "" : "s"})</span>
                    ) : (
                      <>
                        <span className="text-xs text-red-700">{e.verdict}</span>
                        {e.reason && <div className="text-xs text-gray-500 max-w-md">{e.reason}</div>}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="rounded-lg border border-gray-200 bg-white p-5">
        <button
          className="text-sm text-gray-700 hover:text-gray-900"
          onClick={() => setShowManual((v) => !v)}
        >
          {showManual ? "▾" : "▸"} Bind a folder by hand
        </button>
        {showManual && (
          <div className="mt-3">
            <p className="text-sm text-gray-600 mb-3">
              Only needed when a brand's files live somewhere outside the parent. Normally the folder is created
              for you at signup.
            </p>
            <div className="flex gap-2">
              <input
                value={parent}
                onChange={(e) => setParent(e.target.value)}
                placeholder="folder id or share URL to browse"
                className="flex-1 text-sm border border-gray-200 rounded-md px-3 py-2"
              />
              <Button onClick={browse} disabled={busy === "browse"}>
                {busy === "browse" ? "Reading…" : "Browse"}
              </Button>
            </div>
            {subfolders && (
              <div className="mt-4">
                {subfolders.length === 0 && <p className="text-sm text-gray-500">No subfolders here.</p>}
                <ul className="divide-y divide-gray-100">
                  {subfolders.map((s2) => (
                    <li key={s2.id} className="py-2 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm text-gray-900 truncate">{s2.name}</div>
                        <div className="text-xs text-gray-400 font-mono truncate">{s2.id}</div>
                      </div>
                      {s2.bound_to ? (
                        <span className="text-xs text-green-700 bg-green-50 border border-green-200 rounded px-2 py-1 whitespace-nowrap">
                          bound to {s2.bound_to}
                        </span>
                      ) : (
                        <BindControl
                          tenants={tenants}
                          busy={busy === s2.id}
                          onBind={(slug) => bind(s2.id, slug, s2.name)}
                        />
                      )}
                    </li>
                  ))}
                </ul>
                {looseFiles.length > 0 && (
                  <p className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                    {looseFiles.length} file(s) sit directly in this folder. Files outside a brand subfolder have
                    not declared a brand, so they quarantine rather than load.
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </section>

      <p className="text-xs text-gray-500">
        Proposals are reviewed on the brand's own pages, not here — loading a brand's data is the brand's
        decision. The poll runs hourly on its own; "Poll now" only saves the wait.
      </p>
    </div>
  );
}

function WatchBadge({ folder }: { folder: Folder }) {
  if (!folder.watch) return <span className="text-xs text-gray-500">off</span>;
  const styles: Record<Folder["watch_state"], string> = {
    ok: "text-green-700 bg-green-50 border-green-200",
    paused: "text-gray-700 bg-gray-50 border-gray-200",
    error: "text-red-700 bg-red-50 border-red-200",
  };
  return (
    <span className={`text-xs border rounded px-2 py-0.5 ${styles[folder.watch_state]}`}>
      {folder.watch_state}
    </span>
  );
}

function BindControl({
  tenants,
  busy,
  onBind,
}: {
  tenants: { slug: string; display_name: string }[];
  busy: boolean;
  onBind: (slug: string) => void;
}) {
  const [slug, setSlug] = useState("");
  return (
    <div className="flex gap-2 items-center shrink-0">
      <select
        value={slug}
        onChange={(e) => setSlug(e.target.value)}
        className="text-sm border border-gray-200 rounded-md px-2 py-1"
      >
        <option value="">Choose brand…</option>
        {tenants.map((t) => (
          <option key={t.slug} value={t.slug}>
            {t.display_name}
          </option>
        ))}
      </select>
      <Button onClick={() => onBind(slug)} disabled={!slug || busy}>
        {busy ? "Binding…" : "Bind"}
      </Button>
    </div>
  );
}
